import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environment/environment';
import { UploadMetadataDTO } from '../../dto/upload/upload-metadata.dto';
import { UploadInitResponseDTO } from '../../dto/upload/upload-init-response.dto';
import { UploadRequestDTO } from '../../dto/upload/upload-request.dto';
import { ChunkUploadDTO } from '../../dto/upload/chunk-upload.dto';
import { UploadFinalizeDTO } from '../../dto/upload/upload-finalize.dto';
import { 
  FileItemDTO, 
  FileListResponseDTO, 
  FileOperationDTO, 
  FileListRequestDTO,
  FileAttachRequestDTO,
  FileDetachRequestDTO
} from '../../dto/file-manager/file-item.dto';
import { resolveUploadMimeTypeFromFile } from '../../utils/upload-mime';

/**
 * Raw chunk size per request before base64 (512 KiB).
 * test-api.duna.dk rejects POST bodies above ~1 MiB (returns 404 before PHP).
 * Base64 adds ~33% overhead, so 1 MiB raw chunks (~1.4 MiB JSON) still fail.
 */
const UPLOAD_CHUNK_BYTES = 512 * 1024;

@Injectable({
  providedIn: 'root'
})
export class MediaService {
  private readonly API_URL = `${environment.apiUrl}/upload`;
  private readonly FILE_MANAGER_URL = `${environment.apiUrl}/filemanager`;

  constructor(private http: HttpClient) {}

  /**
   * Test chunk upload format with base64 encoding
   */
  async testChunkFormat(sizeInMB: number = 4): Promise<any> {
    // Create test data of specified size
    const testData = 'x'.repeat(sizeInMB * 1024 * 1024);
    
    // Convert to base64 (like real chunks)
    const base64Data = btoa(testData);
    
    
    const response = await firstValueFrom(
      this.http.post<any>(`${this.API_URL}/test-chunk-format`, {
        obfuscated_name: 'test-chunk-format',
        chunk_index: 0,
        chunk_data: base64Data
      })
    );
    
    return response;
  }

  /**
   * Test request size limits
   */
  async testRequestSize(sizeInMB: number = 4): Promise<any> {
    // Create test data of specified size
    const testData = 'x'.repeat(sizeInMB * 1024 * 1024);
    
    const response = await firstValueFrom(
      this.http.post<any>(`${this.API_URL}/test-size`, {
        test_data: testData
      })
    );
    
    return response;
  }

  /**
   * Initialize an upload session
   */
  async abortUpload(obfuscatedName: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.API_URL}/abort`, { obfuscated_name: obfuscatedName })
      );
    } catch (error) {
      console.warn('[MediaService] Failed to abort upload session:', error);
    }
  }

  async initializeUpload(
    file: File,
    chunkSize: number = UPLOAD_CHUNK_BYTES,
    scopeType: 'global' | 'organization' | 'location' | 'user' | 'event' = 'organization',
    scopeId?: string | number,
    eventRole?: string
  ): Promise<UploadInitResponseDTO> {
    const requestData: UploadRequestDTO = {
      original_name: file.name,
      mime_type: resolveUploadMimeTypeFromFile(file),
      file_size: file.size,
      chunk_size: chunkSize,
      scope_type: scopeType,
      scope_id: scopeId,
      event_role: eventRole
    };
    
    
    const response = await firstValueFrom(
      this.http.post<UploadInitResponseDTO>(`${this.API_URL}/init`, requestData)
    );
    
    return response;
  }

  /**
   * Upload file in chunks (RAW - no encryption, backend handles that)
   */
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void,
    onStatusChange?: (status: string) => void
  ): Promise<string> {
    const chunkSize = UPLOAD_CHUNK_BYTES;
    let initResponse: UploadInitResponseDTO | null = null;

    try {
      initResponse = await this.initializeUpload(file, chunkSize);

      const obfuscatedName = await this.uploadChunksAfterInit(
        file,
        initResponse,
        chunkSize,
        onProgress
      );

      if (onStatusChange) {
        onStatusChange('Backend processing...');
      }

      const finalizeData: UploadFinalizeDTO = {
        obfuscated_name: obfuscatedName
      };

      await firstValueFrom(
        this.http.post(`${this.API_URL}/finalize`, finalizeData)
      );

      await this.waitForProcessing(obfuscatedName, onStatusChange);

      return obfuscatedName;
    } catch (error) {
      if (initResponse?.obfuscated_name) {
        await this.abortUpload(initResponse.obfuscated_name);
      }
      throw error;
    }
  }

  private async uploadChunksAfterInit(
    file: File,
    initResponse: UploadInitResponseDTO,
    chunkSize: number,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const totalChunks = initResponse.chunk_count;
    const maxRetries = 3;

    for (let i = 0; i < totalChunks; i++) {
      let uploaded = false;
      
      for (let attempt = 1; attempt <= maxRetries && !uploaded; attempt++) {
        try {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          
          // Read chunk as ArrayBuffer (NO ENCRYPTION)
          const chunkData = await this.readFileAsArrayBuffer(chunk);
          
          // Convert to base64 for transmission
          const base64Chunk = this.arrayBufferToBase64(chunkData);
          
          // Upload chunk using DTO
          const chunkUpload: ChunkUploadDTO = {
            obfuscated_name: initResponse.obfuscated_name,
            chunk_index: i,
            chunk_data: base64Chunk
          };
          
          if (attempt > 1) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          }
          
          await firstValueFrom(
            this.http.post(`${this.API_URL}/chunk`, chunkUpload)
          );
          
          uploaded = true;
          
        } catch (error: any) {
          console.error(`Chunk ${i} upload attempt ${attempt} failed:`, error);

          const apiMessage =
            typeof error.error?.message === 'string' ? error.error.message : '';
          if (
            error.status === 404 &&
            apiMessage.includes('Endpoint not found')
          ) {
            throw new Error(
              'Serveren afviser chunk-upload (request for stor). ' +
                'Typisk skal LimitRequestBody / client_max_body_size og post_max_size hæves på test-api. ' +
                'Kontakt drift hvis fejlen fortsætter.'
            );
          }
          
          // Check for post_max_size error
          if (error.error?.error?.includes('post_max_size')) {
            throw new Error(`Chunk size too large: ${error.error.error}. ${error.error.suggestion}`);
          }
          
          if (attempt === maxRetries) {
            throw new Error(`Failed to upload chunk ${i} after ${maxRetries} attempts: ${error.message}`);
          }
        }
      }
      
      // Report progress
      if (onProgress) {
        const progress = ((i + 1) / totalChunks) * 100;
        onProgress(progress);
      }
    }

    return initResponse.obfuscated_name;
  }

  /**
   * Poll for processing completion
   */
  async waitForProcessing(
    obfuscatedName: string,
    onStatusChange?: (status: string) => void
  ): Promise<void> {
    const maxWait = 300000; // 5 minutes
    const pollInterval = 2000; // 2 seconds
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWait) {
      const status = await firstValueFrom(
        this.http.get<any>(`${this.API_URL}/status/${obfuscatedName}`)
      );
      
      if (onStatusChange) {
        const statusMap: { [key: string]: string } = {
          'processing': 'Behandler fil...',
          'ready': 'Klar!',
          'failed': 'Fejl under behandling'
        };
        onStatusChange(statusMap[status.status] || status.status);
      }
      
      if (status.status === 'ready' || status.status === 'completed') {
        return;
      }
      
      if (status.status === 'failed') {
        throw new Error('File processing failed');
      }
      
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    throw new Error('Processing timeout');
  }

  /**
   * Get file metadata by obfuscated name
   */
  async getMetadata(obfuscatedName: string): Promise<UploadMetadataDTO> {
    return await firstValueFrom(
      this.http.get<UploadMetadataDTO>(`${this.API_URL}/metadata/${obfuscatedName}`)
    );
  }

  /**
   * Download and decrypt file
   */
  async downloadFile(obfuscatedName: string, onProgress?: (progress: number) => void): Promise<Blob> {
    // Get metadata
    const metadata = await this.getMetadata(obfuscatedName);
    
    // Import encryption key
    const encryptionKey = await this.importEncryptionKey(
      this.base64ToArrayBuffer(metadata.encryption_key)
    );
    
    // Download and decrypt all chunks
    const decryptedChunks: ArrayBuffer[] = [];
    
    for (let i = 0; i < metadata.chunk_count; i++) {
      // Construct chunk URL directly from metadata
      const chunkUrl = this.constructChunkUrl(metadata, i);
      
      // Download chunk directly from Backblaze
      const encryptedChunk = await this.downloadChunk(chunkUrl);
      
      // Decrypt chunk
      const decryptedChunk = await this.decryptChunk(encryptedChunk, encryptionKey, i);
      decryptedChunks.push(decryptedChunk);
      
      // Report progress
      if (onProgress) {
        const progress = ((i + 1) / metadata.chunk_count) * 100;
        onProgress(progress);
      }
    }
    
    // Combine chunks into single blob
    return new Blob(decryptedChunks, { type: metadata.mime_type });
  }

  /**
   * Stream video with MediaSource (with fallback to regular download)
   */
  async streamVideo(
    obfuscatedName: string,
    videoElement: HTMLVideoElement,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    // Get metadata
    const metadata = await this.getMetadata(obfuscatedName);
    
    // Debug: Log metadata
    
    // Check if browser supports MediaSource
    if (!('MediaSource' in window)) {
      return this.streamVideoFallback(obfuscatedName, videoElement, metadata, onProgress);
    }
    
    // Use the codec from metadata if available, otherwise fallback to MIME type
    let codecString: string;
    if (metadata.video_codec) {
      codecString = `video/mp4; codecs="${metadata.video_codec}"`;
    } else {
      codecString = this.getCodecString(metadata.mime_type);
    }
    
    // Check if this codec is supported
    if (!MediaSource.isTypeSupported(codecString)) {
      return this.streamVideoFallback(obfuscatedName, videoElement, metadata, onProgress);
    }
    
    
    // Import encryption key
    const encryptionKey = await this.importEncryptionKey(
      this.base64ToArrayBuffer(metadata.encryption_key)
    );
    
    // Create MediaSource
    const mediaSource = new MediaSource();
    videoElement.src = URL.createObjectURL(mediaSource);
    
    return new Promise((resolve, reject) => {
      mediaSource.addEventListener('sourceopen', async () => {
        try {
          const sourceBuffer = mediaSource.addSourceBuffer(codecString);
          
          // Queue for managing async chunk appending
          let isAppending = false;
          const appendQueue: ArrayBuffer[] = [];
          
          const processQueue = () => {
            if (!isAppending && appendQueue.length > 0 && !sourceBuffer.updating) {
              isAppending = true;
              const chunk = appendQueue.shift()!;
              sourceBuffer.appendBuffer(chunk);
            }
          };
          
          let chunksProcessed = 0;
          
          sourceBuffer.addEventListener('updateend', () => {
            isAppending = false;
            chunksProcessed++;
            processQueue();
            
            // Check if all chunks are processed and end stream
            if (appendQueue.length === 0 && chunksProcessed === metadata.chunk_count) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }
            }
          });
          
          sourceBuffer.addEventListener('error', (event) => {
            console.error('SourceBuffer error:', event);
            reject(new Error('SourceBuffer error: ' + (event as any).message));
          });
          
          sourceBuffer.addEventListener('abort', (event) => {
            console.error('SourceBuffer aborted:', event);
            reject(new Error('SourceBuffer aborted'));
          });
          
          // Download and decrypt chunks
          for (let i = 0; i < metadata.chunk_count; i++) {
            const chunkUrl = this.constructChunkUrl(metadata, i);
            const encryptedChunk = await this.downloadChunk(chunkUrl);
            const decryptedChunk = await this.decryptChunk(encryptedChunk, encryptionKey, i);
            
            // Debug: Log first chunk data to verify decryption
            if (i === 0) {
              const view = new Uint8Array(decryptedChunk);
            }
            
            appendQueue.push(decryptedChunk);
            processQueue();
            
            if (onProgress) {
              const progress = ((i + 1) / metadata.chunk_count) * 100;
              onProgress(progress);
            }
          }
          
          
          // Wait for all chunks to be appended
          const checkComplete = setInterval(() => {
            if (chunksProcessed === metadata.chunk_count) {
              clearInterval(checkComplete);
              resolve();
            }
          }, 100);
          
          // Timeout after 30 seconds
          setTimeout(() => {
            clearInterval(checkComplete);
            resolve();
          }, 30000);
        } catch (error) {
          console.error('MediaSource streaming error:', error);
          // Fallback to regular download
          reject(error);
        }
      });
      
      mediaSource.addEventListener('error', (e) => {
        console.error('MediaSource error event:', e);
        reject(new Error('MediaSource error'));
      });
    });
  }
  
  /**
   * Fallback method: Download entire video then play
   */
  private async streamVideoFallback(
    obfuscatedName: string,
    videoElement: HTMLVideoElement,
    metadata: UploadMetadataDTO,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    
    const blob = await this.downloadFile(obfuscatedName, onProgress);
    videoElement.src = URL.createObjectURL(blob);
    videoElement.load();
  }
  
  /**
   * Get proper codec string for MediaSource
   */
  private getCodecString(mimeType: string): string {
    // Common codec strings that browsers support
    const codecMap: { [key: string]: string } = {
      'video/mp4': 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"', // H.264 + AAC
      'video/webm': 'video/webm; codecs="vp8, vorbis"',
      'video/webm; codecs=vp9': 'video/webm; codecs="vp9"'
    };
    
    return codecMap[mimeType] || mimeType;
  }

  // ==================== Crypto Helper Methods (Download Only) ====================

  private async importEncryptionKey(keyData: ArrayBuffer): Promise<CryptoKey> {
    return await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt'] // Only decrypt, not encrypt
    );
  }

  private async decryptChunk(
    data: ArrayBuffer,
    key: CryptoKey,
    chunkIndex: number
  ): Promise<ArrayBuffer> {
    const dataView = new Uint8Array(data);
    
    // Backend format: IV (12 bytes) + tag (16 bytes) + ciphertext
    const iv = dataView.slice(0, 12);
    const tag = dataView.slice(12, 28);
    const ciphertext = dataView.slice(28);
    
    // WebCrypto GCM expects: ciphertext + tag (tag at the end)
    const combined = new Uint8Array(ciphertext.length + tag.length);
    combined.set(ciphertext, 0);
    combined.set(tag, ciphertext.length);
    
    try {
      return await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, tagLength: 128 },
        key,
        combined
      );
    } catch (error) {
      console.error('Decryption failed. Data format:', {
        totalSize: dataView.length,
        ivSize: iv.length,
        tagSize: tag.length,
        ciphertextSize: ciphertext.length,
        combinedSize: combined.length
      });
      throw error;
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Construct direct Backblaze B2 URL for a chunk
   */
  private constructChunkUrl(metadata: UploadMetadataDTO, chunkIndex: number): string {
    const paddedIndex = String(chunkIndex).padStart(5, '0');
    const chunkFileName = `chunk_${paddedIndex}.bin`;
    // Use upload_uuid for B2 folder name, fallback to obfuscated_name for backward compatibility
    const folderName = metadata.upload_uuid || metadata.obfuscated_name;
    const url = `https://f003.backblazeb2.com/file/${metadata.bucket_name}/${folderName}/${chunkFileName}`;
    return url;
  }

  private async downloadChunk(url: string): Promise<ArrayBuffer> {
    
    try {
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      
      if (!response.ok) {
        throw new Error(`Failed to download chunk: ${response.status} ${response.statusText}`);
      }
      
      return await response.arrayBuffer();
    } catch (error: any) {
      console.error('Download chunk error:', error);
      console.error('URL that failed:', url);
      throw error;
    }
  }

  private readFileAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // ==================== File Manager Methods ====================

  /**
   * List files with filtering
   */
  async listFiles(params?: FileListRequestDTO): Promise<FileListResponseDTO> {
    // Use GET request to the collections endpoint since we're not uploading files
    const queryParams: string[] = [];
    
    if (params) {
      if (params.scope_type) queryParams.push(`scope_type=${encodeURIComponent(params.scope_type)}`);
      if (params.scope_id) queryParams.push(`scope_id=${params.scope_id}`);
      if (params.event_role) queryParams.push(`event_role=${encodeURIComponent(params.event_role)}`);
      if (params.include_trash !== undefined) queryParams.push(`include_trash=${params.include_trash}`);
      if (params.parent_folder) queryParams.push(`parent_folder=${encodeURIComponent(params.parent_folder)}`);
      if (params.search_query) queryParams.push(`search_query=${encodeURIComponent(params.search_query)}`);
    }
    
    const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    
    return await firstValueFrom(
      this.http.get<FileListResponseDTO>(`${this.FILE_MANAGER_URL}/collections${queryString}`)
    );
  }

  /**
   * Read single file metadata
   */
  async readFile(obfuscatedName: string): Promise<FileItemDTO> {
    return await firstValueFrom(
      this.http.get<FileItemDTO>(`${this.FILE_MANAGER_URL}/read/${obfuscatedName}`)
    );
  }

  /**
   * Rename file (change display name)
   */
  async renameFile(obfuscatedName: string, newName: string): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName,
      new_name: newName
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/rename_file`, data)
    );
  }

  /**
   * Soft delete file (move to trash)
   */
  async deleteFile(obfuscatedName: string): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/delete_file`, data)
    );
  }

  /**
   * Restore file from trash
   */
  async restoreFile(obfuscatedName: string): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/restore_file`, data)
    );
  }

  /**
   * Move file (change access scope)
   */
  async moveFile(
    obfuscatedName: string, 
    scopeType: string, 
    scopeId?: number, 
    eventRole?: string
  ): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName,
      scope_type: scopeType,
      scope_id: scopeId,
      event_role: eventRole
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/move`, data)
    );
  }

  /**
   * Create access record without moving the file (for sharing)
   */
  async createFileAccess(
    obfuscatedName: string, 
    scopeType: string, 
    scopeId?: string | number, 
    eventRole?: string,
    isReadonly: boolean = true
  ): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName,
      scope_type: scopeType,
      scope_id: scopeId,
      event_role: eventRole,
      is_readonly: isReadonly
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/create_access`, data)
    );
  }

  /**
   * Remove access record without affecting the original file
   */
  async removeFileAccess(
    obfuscatedName: string, 
    scopeType: string, 
    scopeId?: string | number
  ): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName,
      scope_type: scopeType,
      scope_id: scopeId
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/remove_access`, data)
    );
  }

  /**
   * Attach file to event
   */
  async attachToEvent(obfuscatedName: string, eventId: number): Promise<any> {
    const data: FileAttachRequestDTO = {
      obfuscated_name: obfuscatedName,
      event_id: eventId
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/attach`, data)
    );
  }

  /**
   * Detach file from event
   */
  async detachFromEvent(documentId: number): Promise<any> {
    const data: FileDetachRequestDTO = {
      document_id: documentId
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/detach`, data)
    );
  }

  /**
   * Get download URL for file
   */
  getDownloadUrl(obfuscatedName: string): string {
    return `${this.FILE_MANAGER_URL}/download/${obfuscatedName}`;
  }

  // ==================== Virtual Folder Management ====================

  /**
   * Create a virtual folder
   */
  async createFolder(
    folderName: string,
    parentFolder: string = '/',
    scopeType?: string,
    scopeId?: string | number,
    eventRole?: string
  ): Promise<any> {
    const data = {
      folder_name: folderName,
      parent_folder: parentFolder,
      scope_type: scopeType,
      scope_id: scopeId,
      event_role: eventRole
    };

    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/folders`, data)
    );
  }

  /**
   * Rename a virtual folder
   */
  async renameFolder(obfuscatedName: string, newName: string): Promise<any> {
    const data = {
      obfuscated_name: obfuscatedName,
      new_name: newName
    };

    return await firstValueFrom(
      this.http.put(`${this.FILE_MANAGER_URL}/folders/rename`, data)
    );
  }

  /**
   * Delete a virtual folder
   */
  async deleteFolder(obfuscatedName: string): Promise<any> {
    return await firstValueFrom(
      this.http.delete(`${this.FILE_MANAGER_URL}/folders`, {
        body: { obfuscated_name: obfuscatedName }
      })
    );
  }

  async restoreFolder(obfuscatedName: string): Promise<any> {
    const data: FileOperationDTO = {
      obfuscated_name: obfuscatedName
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/folders/restore`, data)
    );
  }

  /**
   * Upload file to a specific folder
   */
  async uploadFileToFolder(
    file: File,
    parentFolder: string = '/',
    scopeType: 'global' | 'organization' | 'location' | 'user' | 'event' = 'organization',
    scopeId?: string | number,
    onProgress?: (progress: number) => void,
    onStatusChange?: (status: string) => void,
    eventRole?: string
  ): Promise<string> {
    const chunkSize = UPLOAD_CHUNK_BYTES;
    let initResponse: UploadInitResponseDTO | null = null;

    try {
      initResponse = await this.initializeUpload(
        file,
        chunkSize,
        scopeType,
        scopeId,
        eventRole
      );

      const obfuscatedName = await this.uploadChunksAfterInit(
        file,
        initResponse,
        chunkSize,
        onProgress
      );

      if (onStatusChange) {
        onStatusChange('Backend processing...');
      }

      const finalizeData: UploadFinalizeDTO = {
        obfuscated_name: obfuscatedName,
        parent_folder: parentFolder
      };

      await firstValueFrom(
        this.http.post(`${this.API_URL}/finalize`, finalizeData)
      );

      await this.waitForProcessing(obfuscatedName, onStatusChange);

      if (parentFolder && parentFolder !== '/') {
        try {
          await this.setFileParentFolder(obfuscatedName, parentFolder);
        } catch (error) {
          console.warn('Failed to set file parent folder:', error);
        }
      }

      return obfuscatedName;
    } catch (error) {
      if (initResponse?.obfuscated_name) {
        await this.abortUpload(initResponse.obfuscated_name);
      }
      throw error;
    }
  }

  /**
   • Set file parent folder using file manager
   */
  async setFileParentFolder(obfuscatedName: string, parentFolder: string): Promise<any> {
    const data = {
      obfuscated_name: obfuscatedName,
      parent_folder: parentFolder
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/set_file_parent_folder`, data)
    );
  }

  /**
   • Move file to different folder
   */
  async moveFileToFolder(obfuscatedName: string, targetFolder: string): Promise<any> {
    const data = {
      obfuscated_name: obfuscatedName,
      target_folder: targetFolder
    };
    
    return await firstValueFrom(
      this.http.post(`${this.FILE_MANAGER_URL}/move_file_to_folder`, data)
    );
  }
}
